import type { RoleConfig, RoleName } from './types';

export const NOMBRE_TO_ROLE: Record<string, RoleName> = {
  // Owner profiles share AdminShell. Keep both names explicit so their
  // personalized labels remain correct while permissions stay identical.
  'Christopher Vesco': 'chris',
  'Ileana Pinto': 'ileana',
  // Dr. Murillo is read-only; route him through Arnoldo's old shell for now.
  // When the dedicated vet shell exists, add a 'murillo' RoleName + view.
  'Dr. Murillo': 'arnoldo',
};

/**
 * Resolve role from server-provided rol + nombre.
 *
 * After the 2026-05-18 cleanup, DB `rol` is one of: owner | manager | field | readonly.
 * The 4 vaqueros (Jose, Ricardo, Socorro, Yader) all carry rol='field' but are
 * NOT in NOMBRE_TO_ROLE, so they fall through to the vaquero shell.
 *
 * Glenda is also rol='field' but explicitly mapped by name so she gets her own
 * full-app shell instead of the stripped-down vaquero one.
 */
export function resolveRole(nombre: string, rol: string | null | undefined): RoleName {
  // Explicit name match wins for personalized owner/vet profiles.
  if (NOMBRE_TO_ROLE[nombre]) return NOMBRE_TO_ROLE[nombre];
  const r = (rol || '').trim().toLowerCase();
  // Every owner must reach AdminShell. Named owners resolve above; this
  // fallback prevents a future owner account from landing in a field shell.
  if (r === 'owner') return 'chris';
  // Sprint 3.5b — coordinator tier (Alejandra + Glenda) lands in AdminShell.
  // They can still flip into the vaquero view via the 'Vista de campo' link
  // in the AdminShell sidebar (handled by setRole at the AuthGate level).
  if (r === 'coordinator') return 'coordinator';
  // Anyone else with rol in (field, manager, readonly) gets the vaquero
  // shell. The server enforces write permissions — readonly users will see
  // the same UI but their submits return 403.
  if (r === 'field' || r === 'manager' || r === 'readonly') return 'vaquero';
  // Last-resort default
  return 'glenda';
}

export const ROLES: Record<RoleName, RoleConfig> = {
  coordinator: {
    // Placeholder display config — the AdminShell renders its own sidebar
    // and header so most fields here are unused. Kept for type completeness
    // and for the brief moment between auth and shell mount.
    view: 'view-glenda',
    name: 'Coordinación',
    role: 'Coordinación · Los Álamos',
    letter: 'C',
    color: 'var(--status-positive)',
    fab: false,
    hoyLabel: 'Hoy',
    financial: false,
  },
  glenda: {
    view: 'view-glenda',
    name: 'Glenda Martínez',
    role: 'Colaborador de Campo · Los Álamos',
    letter: 'G',
    color: 'var(--status-positive)',
    fab: true,
    hoyLabel: 'Hoy',
    financial: false,
  },
  alejandra: {
    view: 'view-glenda',
    name: 'Alejandra Quesada',
    role: 'Administradora · Los Álamos',
    letter: 'A',
    color: 'var(--status-positive)',
    fab: true,
    hoyLabel: 'Hoy',
    financial: false,
  },
  arnoldo: {
    view: 'view-arnoldo',
    name: 'Arnoldo Cedeño',
    role: 'Solo lectura · Los Álamos',
    letter: 'A',
    color: 'var(--text-tertiary)',
    fab: false,
    hoyLabel: 'Hoy',
    financial: false,
  },
  chris: {
    view: 'view-chris',
    name: 'Christopher Vesco',
    role: 'Consultor · Remoto',
    letter: 'C',
    color: 'var(--status-info)',
    fab: false,
    hoyLabel: 'Resumen',
    financial: true,
  },
  ileana: {
    view: 'view-chris',
    name: 'Ileana Pinto',
    role: 'Dueña · Remoto',
    letter: 'I',
    color: 'var(--status-info)',
    fab: false,
    hoyLabel: 'Resumen',
    financial: true,
  },
  vaquero: {
    view: 'view-vaquero',
    name: 'Operador Lechería',
    role: 'Operador Lechería',
    letter: 'O',
    color: 'var(--status-positive)',
    fab: false,
    hoyLabel: 'Hoy',
    financial: false,
  },
};

/**
 * RoleName → permission level used by route guards (PermissionGate, FAB,
 * ManejoPage, registry.requiredPermissions). Keep this in one place so all
 * three gates agree.
 */
export const ROLE_TO_PERM: Record<RoleName, 'owner' | 'manager' | 'field' | 'readonly'> = {
  glenda: 'field',
  alejandra: 'manager',
  arnoldo: 'readonly',
  chris: 'owner',
  ileana: 'owner',
  vaquero: 'field',
  // Sprint 3.5b — coordinator carries 'manager' permission level so any
  // existing PermissionGate (manejo/sesiones routes) treats Alejandra and
  // Glenda as authorized writers when they flip into the vaquero view.
  coordinator: 'manager',
};

export function getPermLevel(role: RoleName): string {
  return ROLE_TO_PERM[role] ?? 'field';
}

export function hasPermission(
  userPermissions: string[],
  required: string[],
): boolean {
  return required.some((p) => userPermissions.includes(p));
}

export function getRoleFromName(name: string): RoleName {
  return NOMBRE_TO_ROLE[name] ?? 'glenda';
}

export function getRoleConfig(role: RoleName): RoleConfig {
  return ROLES[role];
}
